import React from 'react';
import * as Scrivito from 'scrivito';
import { Helmet } from 'react-helmet-async';
import { currentOrigin, toAbsoluteUrl } from '../lib/origin';

function absoluteUrl(page) {
  const origin = currentOrigin();
  if (!origin) return null;
  const path = Scrivito.urlFor(page);
  return path ? toAbsoluteUrl(path, origin) : null;
}

function absolutize(url) {
  if (!url) return url;
  const origin = currentOrigin();
  if (!origin) return url;
  return toAbsoluteUrl(url, origin);
}

// Generated here, centrally, rather than per Obj class: an SEO audit flagged
// the whole site for having no BreadcrumbList schema anywhere, and every page
// using SeoHead already has what this needs (its own ancestors chain) without
// having to declare it again per template. Skipped for a bare Homepage - a
// single-item trail ("Home") states nothing a breadcrumb should, and every
// current Page is a direct Homepage child anyway (see Page/index.jsx), so
// this never needs to go deeper than one hop.
function breadcrumbSchema(page, lang) {
  const origin = currentOrigin();
  if (!origin) return null;

  // page.ancestors() includes RootPage (src/Objs/RootPage) above every
  // Homepage - a structural node with no editorial title, existing only to
  // redirect "/" client-side. Filtered out here rather than skipped in
  // RootPage's own component, since a breadcrumb is the only place its
  // presence in the tree would otherwise leak into rendered markup as a
  // blank, unnamed crumb.
  const chain = [
    ...page.ancestors().filter((obj) => obj && ['Homepage', 'Page'].includes(obj.objClass())),
    page,
  ];
  if (chain.length < 2) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: chain.map((obj, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: obj.objClass() === 'Homepage' ? (lang === 'de' ? 'Startseite' : 'Home') : obj.get('title') || '',
      item: toAbsoluteUrl(Scrivito.urlFor(obj), origin),
    })),
  };
}

export default function SeoHead({
  page,
  lang,
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  structuredData,
  children,
}) {
  const canonicalUrl = absoluteUrl(page);
  const absoluteOgImage = absolutize(ogImage);
  const breadcrumbs = breadcrumbSchema(page, lang);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="trans-tech" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {absoluteOgImage && <meta property="og:image" content={absoluteOgImage} />}

      <meta name="twitter:card" content={absoluteOgImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {absoluteOgImage && <meta name="twitter:image" content={absoluteOgImage} />}

      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
      {breadcrumbs && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      )}

      {children}
    </Helmet>
  );
}
